<template>
  <div
    class="bg-white border border-grayLight rounded-[12px] p-5 h-full flex flex-col"
  >
    <div class="flex items-center mb-4">
      <h3 class="text-[14px] font-semibold text-bodyText">Teams</h3>
      <router-link
        to="/teams"
        class="ml-1 text-grayDark hover:text-primaryDarkBg flex items-center"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.25 10.5L8.75 7L5.25 3.5"
            stroke="#9CA1A3"
            stroke-width="1.33333"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div
        class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primaryDarkBg"
      ></div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="teams.length === 0"
      class="flex-1 flex items-center justify-center"
    >
      <p class="text-[13px] text-grayDark">No teams found.</p>
    </div>

    <!-- Table -->
    <div v-else class="flex-1 overflow-auto">
      <table class="w-full">
        <thead class="bg-[#F9FAFB]">
          <tr class="border-b border-grayLight">
            <th
              class="text-left text-[12px] text-grayDark font-medium py-2 pr-4 pl-2 rounded-tl-[6px]"
            >
              Name
            </th>
            <th
              class="text-left text-[12px] text-grayDark font-medium py-2 pr-4"
            >
              Talents
            </th>
            <th class="text-left text-[12px] text-grayDark font-medium py-2 rounded-tr-[6px]">
              Projects
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(team, index) in limitedTeams"
            :key="team.team_id"
            class="border-b border-grayLight last:border-0"
          >
            <td class="py-3 pr-4">
              <div class="flex items-center gap-2">
                <span
                  class="w-[3px] h-[18px] rounded-full flex-shrink-0"
                  :class="teamColorBorder(index)"
                ></span>
                <span
                  class="text-[13px] text-bodyText truncate max-w-[100px]"
                  >{{ team.team_name }}</span
                >
              </div>
            </td>
            <td class="py-3 pr-4">
              <div class="flex items-center">
                <div class="flex -space-x-2">
                  <div
                    v-for="(member, mIdx) in (team.members || []).slice(0, 4)"
                    :key="mIdx"
                    class="relative group/avatar"
                  >
                    <img
                      :src="member.profile_picture || defaultAvatar"
                      :alt="member.name || 'Member'"
                      class="w-7 h-7 rounded-full border-2 border-white object-cover"
                      @error="handleAvatarError($event)"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none z-10">
                      {{ member.name  }}
                      <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div
                    v-if="(team.members || []).length > 4"
                    class="w-7 h-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-[9px] text-grayDark font-medium"
                  >
                    +{{ (team.members || []).length - 4 }}
                  </div>
                </div>
                <span v-if="(team.members || []).length === 0" class="text-[12px] text-grayDark">—</span>
              </div>
            </td>
            <td class="py-3">
              <span class="text-[13px] text-bodyText">{{
                team.task_count ?? 0
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";

const BORDER_COLORS = [
  "bg-green-500",
  "bg-yellow-400",
  "bg-red-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-pink-400",
];

export default {
  name: "DashboardTeamsCard",
  data() {
    return {
      teams: [],
      loading: true,
    };
  },
  computed: {
    limitedTeams() {
      return this.teams.slice(0, 5);
    },
    defaultAvatar() {
      return require("@/assets/users/default-profile-picture.jpg");
    },
  },
  methods: {
    teamColorBorder(index) {
      return BORDER_COLORS[index % BORDER_COLORS.length];
    },
    handleAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },
    async fetchTeams() {
      try {
        this.loading = true;
        const response = await apiClient.get("/web/teams/manager/teams", {
          params: { page_size: 5 },
        });
        this.teams = response.data.data || [];
      } catch (error) {
        console.error("Error fetching teams:", error);
        this.teams = [];
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchTeams();
  },
};
</script>
